import { useLazyQuery } from '@apollo/client';
import { type Remote, wrap } from 'comlink';
import { useEffect, useRef, useState } from 'react';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import { REPORT_TYPES } from '../../../constants';
import {
  SECURE_REPORT_DOWNLOAD_QUERY,
  type SecureReportDownloadQueryData,
} from '../../../hooks/useReportDownloads';
import type { AssetIdentifier, ReportState } from '../types';
import { finalizeAssetReportHtml, sanitizeAssetReportHtml } from '../utils';
import type { AssetReportSanitizerWorker } from './assetReportSanitizer.worker';

const REPORT_RENDER_ERROR_MESSAGE =
  'This report could not be rendered. Please try downloading the Asset Report to view.';
const REPORT_UNAVAILABLE_MESSAGE = 'This report is not available.';

type UseAssetReportHtmlParams = {
  asset: AssetIdentifier | null;
};

type UseAssetReportHtmlReturn = ReportState & {
  shouldCleanDom: boolean;
  resetCleanDom: () => void;
  loadReport: () => void;
};

const useAssetReportHtml = ({ asset }: UseAssetReportHtmlParams): UseAssetReportHtmlReturn => {
  const { setSnack } = useSnackBarContext();
  const [reportState, setReportState] = useState<ReportState>({
    html: null,
    status: 'idle',
    error: null,
  });
  const [shouldCleanDom, setShouldCleanDom] = useState(false);
  const previousAssetIdRef = useRef<string | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const sanitizerRef = useRef<Remote<AssetReportSanitizerWorker> | null>(null);

  const [getHtmlReport, { loading }] = useLazyQuery<SecureReportDownloadQueryData>(
    SECURE_REPORT_DOWNLOAD_QUERY,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const worker = new Worker(new URL('./assetReportSanitizer.worker.ts', import.meta.url), {
      type: 'module',
    });

    workerRef.current = worker;
    sanitizerRef.current = wrap<AssetReportSanitizerWorker>(worker);

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
      sanitizerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (asset?.legacyId === previousAssetIdRef.current) {
      return;
    }

    previousAssetIdRef.current = asset?.legacyId ?? null;
    setReportState({
      html: null,
      status: 'idle',
      error: null,
    });
  }, [asset?.legacyId]);

  const loadReport = async (): Promise<void> => {
    if (!asset) {
      return;
    }

    if (!asset.isoCode) {
      setReportState({
        html: REPORT_UNAVAILABLE_MESSAGE,
        status: 'unavailable',
        error: null,
      });
      return;
    }

    setReportState(prev => ({
      ...prev,
      status: 'loading',
    }));

    try {
      const { data } = await getHtmlReport({
        variables: {
          assetLegacyId: asset.legacyId,
          isoCode: asset.isoCode,
          reportType: REPORT_TYPES.ENHANCED_HTML_REPORT,
        },
      });

      const downloadUrl = data?.secureReportDownload?.url;
      if (!downloadUrl) {
        setReportState({
          html: REPORT_UNAVAILABLE_MESSAGE,
          status: 'unavailable',
          error: null,
        });
        return;
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error('Unable to load the Asset Report preview.');
      }

      const rawHtml = await response.text();

      let sanitizedHtml = '';
      const sanitizer = sanitizerRef.current;

      if (sanitizer) {
        try {
          const preprocessedHtml = await sanitizer.sanitizeHtml(rawHtml);
          sanitizedHtml = finalizeAssetReportHtml(preprocessedHtml);
        } catch (_sanitizerError) {
          sanitizedHtml = sanitizeAssetReportHtml(rawHtml);
        }
      } else {
        sanitizedHtml = sanitizeAssetReportHtml(rawHtml);
      }

      setReportState({
        html: sanitizedHtml,
        status: 'success',
        error: null,
      });
      setShouldCleanDom(true);
    } catch (error) {
      const errorInstance = error instanceof Error ? error : new Error(REPORT_RENDER_ERROR_MESSAGE);

      setReportState({
        html: REPORT_RENDER_ERROR_MESSAGE,
        status: 'error',
        error: errorInstance,
      });

      setSnack({
        open: true,
        severity: 'error',
        message: errorInstance.message,
      });
    }
  };

  const resetCleanDom = (): void => {
    setShouldCleanDom(false);
  };

  return {
    ...reportState,
    status: loading ? 'loading' : reportState.status,
    shouldCleanDom,
    resetCleanDom,
    loadReport,
  };
};

export { useAssetReportHtml, REPORT_RENDER_ERROR_MESSAGE, REPORT_UNAVAILABLE_MESSAGE };
